<template>
  <h2
    class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
    style="color: var(--text-color)"
  >
    Podcasters Management
  </h2>
  <div>
    <div class="flex tw-justify-end gap-2 mb-4 p-2">
      <Button
        :class="[
          'p-button-sm',
          activeTab === 'all' ? 'p-button-primary' : 'p-button-outlined',
        ]"
        @click="switchTab('all')"
      >
        All Podcasters
      </Button>
      <Button
        :class="[
          'p-button-sm',
          activeTab === 'pending' ? 'p-button-primary' : 'p-button-outlined',
        ]"
        @click="switchTab('pending')"
      >
        Pending Podcasters
      </Button>
    </div>
    <Card>
      <template #header>
        <div
          class="flex flex-wrap justify-content-between align-items-center p-2 gap-2"
        >
          <div class="flex flex-wrap gap-2">
            <Button
              class="p-button-sm"
              @click="goToCreateNewPodcaster()"
              v-if="activeTab === 'all'"
              >Add New Podcaster</Button
            >
            <Button
              class="p-button-sm"
              @click="showUploadCSVModal(true)"
              v-if="activeTab === 'all'"
              >Add Network</Button
            >
          </div>
          <InputText
            class="w-full sm:w-auto"
            type="text"
            v-model="podcastersTableFilter.global.value"
            placeholder="Global Search"
          />
        </div>
      </template>
      <template #content>
        <div
          v-if="networkFilterId"
          class="tw-flex tw-justify-between tw-items-center tw-rounded tw-p-2 tw-mb-2 tw-bg-surface-0"
        >
          <span>
            Showing podcasters for network
            <span class="tw-font-semibold">
              {{ networkFilterName || networkFilterId }}
            </span>
          </span>
          <Button
            label="Clear"
            class="p-button-sm p-button-outlined"
            severity="secondary"
            @click="clearNetworkFilter"
          />
        </div>
        <DataTable
          :value="displayedPodcasters"
          :paginator="true"
          :rows="10"
          :first="initFirst"
          @page="onChangePage"
          :filters="podcastersTableFilter"
          class="p-datatable-sm"
          :stripedRows="true"
          :globalFilterFields="
            activeTab === 'all'
              ? [
                  'id',
                  'name',
                  'email',
                  'status',
                  'podcaster_type',
                  'plan',
                  'utm_source',
                  'network_name',
                ]
              : [
                  'id',
                  'name',
                  'email',
                  'referrer_email',
                  'referrer_name',
                  'network_name',
                  'network_event_name',
                ]
          "
        >
          <Column field="id" header="ID" :sortable="true" style="width: 5%">
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.id">
                {{ slotProps.data.id }}
              </div>
            </template>
          </Column>
          <Column
            field="name"
            header="Name"
            :sortable="true"
            style="width: 15%"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.name">
                {{ slotProps.data.name }}
              </div>
            </template>
          </Column>
          <Column
            field="email"
            header="Email"
            :sortable="true"
            :style="{ width: activeTab === 'all' ? '20%' : '15%' }"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.email">
                {{ slotProps.data.email }}
              </div>
            </template>
          </Column>
          <Column
            field="created_at"
            header="Created At"
            :sortable="true"
            :style="{ width: activeTab === 'all' ? '10%' : '15%' }"
          >
            <template #body="slotProps">
              <div class="date-cell" :title="slotProps.data.created_at">
                {{
                  slotProps.data.created_at || slotProps.data.last_fetched || ""
                }}
              </div>
            </template>
          </Column>
          <Column
            v-if="activeTab === 'all'"
            field="status"
            header="Status"
            :sortable="true"
            style="width: 8%"
          ></Column>
          <Column
            v-if="activeTab === 'all'"
            field="podcaster_type"
            header="Type"
            :sortable="true"
            style="width: 8%"
          ></Column>
          <Column
            v-if="activeTab === 'all'"
            field="plan"
            header="Plan"
            :sortable="true"
            style="width: 8%"
          ></Column>
          <Column
            v-if="activeTab === 'all'"
            field="network_name"
            header="Network"
            :sortable="true"
            style="width: 10%"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.network_name">
                {{ slotProps.data.network_name || "-" }}
              </div>
            </template>
          </Column>
          <Column
            field="utm_source"
            header="UTM Source"
            :sortable="true"
            style="width: 8%"
            v-if="activeTab === 'all'"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.utm_source">
                {{ slotProps.data.utm_source }}
              </div>
            </template>
          </Column>
          <Column
            v-if="activeTab === 'pending'"
            field="status"
            header="Status"
            :sortable="false"
            style="width: 10%"
          >
            <template #body="slotProps">
              <span
                :style="
                  slotProps.data.status === 'approved'
                    ? 'color:#55ffa5;font-weight:bold'
                    : slotProps.data.status === 'rejected'
                    ? 'color:#ffa7fb;font-weight:bold'
                    : ''
                "
                :class="
                  slotProps.data.status === 'pending'
                    ? 'tw-text-amber-300 font-bold'
                    : ''
                "
              >
                {{ slotProps.data.status }}
              </span>
            </template>
          </Column>

          <Column
            v-if="activeTab === 'pending'"
            field="referrer_name"
            header="Referrer Name"
            :sortable="false"
            style="width: 15%"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.referrer_name">
                {{ slotProps.data.referrer_name || "-" }}
              </div>
            </template>
          </Column>
          <Column
            v-if="activeTab === 'pending'"
            field="network_name"
            header="Network Name"
            style="width: 15%"
            :sortable="false"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.network_name">
                {{ slotProps.data.network_name || "-" }}
              </div>
            </template>
          </Column>
          <Column
            v-if="activeTab === 'pending'"
            field="network_event_name"
            header="Event"
            style="width: 15%"
            :sortable="false"
          >
            <template #body="slotProps">
              <div
                class="truncate-text"
                :title="slotProps.data.network_event_name"
              >
                {{ slotProps.data.network_event_name || "-" }}
              </div>
            </template>
          </Column>
          <Column header="Actions" style="width: 16%">
            <template #body="slotProps">
              <div class="flex gap-1">
                <Button
                  icon="pi pi-rss"
                  class="p-button-sm p-button-outlined"
                  @click="
                    showRSSFeeds(
                      activeTab === 'all'
                        ? slotProps.data.podcaster_id
                        : slotProps.data.id
                    )
                  "
                  title="View RSS Feeds"
                  :disabled="activeTab === 'pending' && !slotProps.data.rss_url"
                >
                  <small class="text-[10px] font-normal align-middle"
                    >RSS</small
                  >
                </Button>

                <template v-if="activeTab === 'pending'">
                  <Button
                    icon="pi pi-credit-card"
                    class="p-button-sm p-button-outlined"
                    title="Copy Banking Form Link"
                    @click="
                      copyBankingLink(getPendingPodcasterId(slotProps.data))
                    "
                  />

                  <Button
                    v-if="
                      slotProps.data.podcaster_id &&
                      slotProps.data.status == 'approved'
                    "
                    icon="pi pi-search"
                    class="p-button-sm p-button-outlined"
                    title="View Podcaster"
                    @click="viewPodcaster(slotProps.data.email)"
                  />
                </template>

                <template v-if="activeTab === 'all'">
                  <Button
                    icon="pi pi-list"
                    class="p-button-sm p-button-outlined"
                    @click="goToShows(slotProps.data.podcaster_id)"
                    title="Shows"
                  />
                  <Button
                    icon="pi pi-megaphone"
                    class="p-button-sm p-button-outlined"
                    @click="goToCampaigns(slotProps.data.podcaster_id)"
                    title="Campaigns"
                  />
                  <Button
                    icon="pi pi-pencil"
                    class="p-button-sm p-button-outlined"
                    @click="goToUpdatePodcaster(slotProps.data.podcaster_id)"
                    title="Edit"
                  />
                  <Button
                    icon="pi pi-trash"
                    class="p-button-sm p-button-outlined p-button-danger"
                    @click="confirmDeletePodcaster(slotProps.data.podcaster_id)"
                    title="Delete"
                  />
                  <Button
                    icon="pi pi-download"
                    class="p-button-sm p-button-outlined"
                    @click="downloadReport(slotProps.data.podcaster_id)"
                    :loading="downloadingReports[slotProps.data.podcaster_id]"
                    :disabled="downloadingReports[slotProps.data.podcaster_id]"
                    title="Download Report"
                  />
                  <Button
                    icon="pi pi-eye"
                    class="p-button-sm p-button-outlined"
                    @click="goToPodcasterOverview(slotProps.data.podcaster_id)"
                    title="Podcaster Overview"
                  />
                  <Button
                    icon="pi pi-link"
                    class="p-button-sm p-button-outlined"
                    title="Get Affiliate Link"
                    @click="getAffiliateLink(slotProps.data.podcaster_id)"
                  />
                  <Button
                    icon="pi pi-chart-bar"
                    class="p-button-sm p-button-outlined"
                    title="Podcaster Dashboard"
                    @click="goToPodcasterDashboard(slotProps.data)"
                  />
                </template>
                <template v-else>
                  <Button
                    v-if="slotProps.data.status === 'pending'"
                    icon="pi pi-user-plus"
                    class="p-button-sm p-button-success"
                    title="Approve"
                    @click="openApproveModal(slotProps.data)"
                    :disabled="approveRejectLoading"
                  />
                  <Button
                    v-if="slotProps.data.status === 'pending'"
                    icon="pi pi-user-minus"
                    class="p-button-sm p-button-danger"
                    title="Reject"
                    @click="openRejectConfirm(slotProps.data)"
                    :disabled="approveRejectLoading"
                  />

                  <Button
                    v-if="
                      slotProps.data.network_name &&
                      slotProps.data.status !== 'approved'
                    "
                    icon="pi pi-envelope"
                    :disabled="slotProps.data.is_invite_email_sent"
                    class="p-button-sm p-button-outlined p-button-info"
                    title="Send an email"
                    @click="openSendEmailModal(slotProps.data)"
                  />
                </template>
              </div>
            </template>
          </Column>
          <template #paginatorstart>
            <Button
              type="button"
              icon="pi pi-refresh"
              @click="fetchPodcasters()"
              text
            />
          </template>
          <template #paginatorend>
            <div class="flex gap-2 tw-items-center">
              <Button
                v-if="activeTab === 'all'"
                type="button"
                icon="pi pi-credit-card"
                text
                @click="openBankingDownloadModal = true"
              />
              <download-csv
                :data="podcastersDownload"
                class="download-csv"
                v-if="activeTab === 'all'"
              >
                <Button type="button" icon="pi pi-download" text />
              </download-csv>
            </div>
          </template>
        </DataTable>
      </template>
    </Card>
    <Dialog v-model:visible="openRssFeeds" header="RSS Feeds" modal>
      <div style="min-width: 200px">
        <div v-if="!rssFeedsForModal.length" class="text-500 my-2">
          No RSS feeds submitted yet.
        </div>
        <div
          v-for="(feed, index) in rssFeedsForModal"
          :key="index"
          class="flex align-items-center gap-2 my-2"
        >
          <a :href="feed.rss_url" target="_blank" class="flex-1">
            {{ feed.rss_url }}
          </a>
          <span v-if="feed.author">({{ feed.author }})</span>
          <Button
            icon="pi pi-copy"
            class="p-button-sm p-button-text"
            @click="copyToClipboard(feed.rss_url)"
            title="Copy RSS URL"
          />
        </div>
      </div>
    </Dialog>
    <Dialog
      v-model:visible="openUploadCSVModal"
      header="Upload CSV"
      modal
      :style="{ width: '20rem' }"
    >
      <p>Upload csv file to add network</p>
      <FileUpload
        class="w-full mt-4"
        ref="fileUpload"
        mode="basic"
        accept="text/csv"
        :maxFileSize="1000000"
        @select="onSelect"
        customUpload
      />
      <p v-if="showMessage.isShow" class="text-red-400">
        {{ showMessage.message }}
      </p>
      <download-csv :data="sampleCSVDownload" class="mt-2">
        (<span
          class="text-blue-500 hover:text-blue-300 underline cursor-pointer"
          >Download sample csv file</span
        >)
      </download-csv>
      <div class="mt-6 flex justify-content-between">
        <Button
          class="w-full sm:w-auto justify-content-center"
          severity="danger"
          @click="showUploadCSVModal(false)"
          >Cancel
        </Button>
        <Button
          class="w-full sm:w-auto justify-content-center"
          @click="submitCSV()"
          >Submit
        </Button>
      </div>
    </Dialog>
    <Dialog
      v-model:visible="approveModalVisible"
      header="Approve Podcaster"
      modal
      :style="{ width: '800px', maxWidth: '90vw' }"
    >
      <div class="tw-min-w-0">
        <div class="grid">
          <div class="col-12 md:col-6 flex flex-column gap-1">
            <label for="rss-input">RSS Feed Link</label>
            <InputText
              id="rss-input"
              v-model="approveModalRss"
              class="w-full"
              placeholder="RSS feed link"
              :class="{ 'p-invalid': approveModalError && !approveModalRss }"
            />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1">
            <label for="podcaster-type">Type</label>
            <Dropdown
              id="podcaster-type"
              v-model="approveModalType"
              :options="podcasterTypeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Type"
              class="w-full"
            />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1">
            <label for="podcaster-plan">Plan</label>
            <Dropdown
              id="podcaster-plan"
              v-model="approveModalPlan"
              :options="podcasterPlanOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Plan"
              class="w-full"
            />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1">
            <label for="revenue-share">Revenue Share</label>
            <InputNumber
              id="revenue-share"
              v-model="approveModalRevShare"
              :minFractionDigits="2"
              :maxFractionDigits="2"
              placeholder="Revenue share percentage"
              class="w-full"
            />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1">
            <label for="hosted-type">Hosted</label>
            <Dropdown
              id="hosted-type"
              v-model="approveModalHosted"
              :options="hostedOptionsSet"
              optionLabel="name"
              optionValue="value"
              placeholder="Select Hosted Type"
              class="w-full"
            />
          </div>
          <div class="col-12 md:col-6 flex flex-column gap-1">
            <label for="avg-downloads">Avg Monthly Downloads</label>
            <InputNumber
              id="avg-downloads"
              v-model="approveModalAvgDownloads"
              :useGrouping="true"
              :max="MAX_AVG_MONTHLY_DOWNLOADS"
              placeholder="Enter avg monthly downloads"
              class="w-full"
              :class="{
                'p-invalid': approveModalError && !approveModalAvgDownloads,
              }"
            />
          </div>
        </div>

        <div class="mt-2">
          <CampaignTypesForm
            v-model:campaigns="approveModalCampaigns"
            :show-error="!!approveModalError"
          />
        </div>

        <span class="text-red-500 text-sm block" :style="{ minHeight: '21px' }">
          <i v-if="approveModalError">{{ approveModalError }}</i>
        </span>

        <div class="flex gap-2 mt-2 justify-end">
          <Button
            label="Cancel"
            @click="handleApproveCancel"
            class="p-button-text"
          />
          <Button
            label="Approve"
            @click="handleApprove"
            class="p-button-success"
            :loading="approveRejectLoading"
          />
        </div>
      </div>
    </Dialog>
    <Dialog
      v-model:visible="rejectConfirmVisible"
      header="Reject Podcaster"
      modal
    >
      <div>
        <p>Are you sure you want to reject this podcaster?</p>
        <div class="flex gap-2 mt-4 justify-end">
          <Button
            label="Cancel"
            @click="handleRejectCancel"
            class="p-button-text"
          />
          <Button
            label="Reject"
            @click="handleReject"
            class="p-button-danger"
          />
        </div>
      </div>
    </Dialog>
    <Dialog
      v-model:visible="openBankingDownloadModal"
      header="Download banking information"
      modal
    >
      <div class="flex flex-column gap-4" style="min-width: 400px">
        <div>
          <label for="banking-number" class="block mb-2"
            >Get Newest Podcasters</label
          >
          <InputText
            id="banking-number"
            type="number"
            v-model.number="bankingDownloadNumber"
            :disabled="bankingDownloadAll || bankingDownloadLoading"
            class="w-full"
            min="1"
          />
        </div>
        <div class="flex gap-3 tw-items-end">
          <label for="banking-download-all">Download all</label>
          <Checkbox
            id="banking-download-all"
            v-model="bankingDownloadAll"
            :binary="true"
            inputId="banking-download-all"
            :disabled="bankingDownloadLoading"
          />
        </div>
      </div>
      <div class="flex gap-2 justify-end mt-4">
        <Button
          label="Cancel"
          @click="openBankingDownloadModal = false"
          class="p-button-text"
          :disabled="bankingDownloadLoading"
        />
        <Button
          label="Download"
          @click="handleBankingDownload"
          class="p-button-success"
          :loading="bankingDownloadLoading"
          :disabled="bankingDownloadLoading"
        />
      </div>
    </Dialog>
    <!-- Send Email Modal -->
    <Dialog
      v-model:visible="sendEmailModalVisible"
      header="Send Email"
      modal
      :style="{ width: '500px', maxWidth: '90vw' }"
    >
      <div class="tw-min-w-0">
        <div class="flex flex-column gap-3">
          <div class="flex flex-column gap-2">
            <label for="email-template">Email Template</label>
            <Dropdown
              id="email-template"
              v-model="selectedEmailTemplate"
              :options="emailTemplateOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Email Template"
              class="w-full"
              @change="onTemplateChange"
            />
          </div>

          <div
            v-if="templateRequirements.includes('url')"
            class="flex flex-column gap-2"
          >
            <label for="email-url">URL</label>
            <InputText
              id="email-url"
              v-model="emailFormData.url"
              class="w-full"
              placeholder="Enter URL"
            />
          </div>
        </div>

        <div class="flex gap-2 mt-4 justify-end">
          <Button
            label="Cancel"
            @click="handleSendEmailCancel"
            class="p-button-text"
          />
          <Button
            label="Send"
            @click="handleSendEmailSubmit"
            class="p-button-primary"
            :loading="sendEmailLoading"
            :disabled="!isFormValid"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import Card from "primevue/card";
import Checkbox from "primevue/checkbox";
import Button from "primevue/button";
import CampaignTypesForm from "./CampaignTypesForm.vue";
import FileUpload from "primevue/fileupload";
import { ref, computed, onBeforeMount, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { FilterMatchMode } from "primevue/api";
import axios from "axios";
import {
  API_BASE_URL,
  hostedOptionsSet,
  podcasterPlanOptions,
  podcasterTypeOptions,
  PODCASTER_JOIN_NETWORK_EMAIL_TEMPLATES,
} from "@/consts";
import Dialog from "primevue/dialog";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Dropdown from "primevue/dropdown";
import Papa from "papaparse";

const MAX_AVG_MONTHLY_DOWNLOADS = 1_000_000_000;

let tempFirst = 0;
const initFirst = ref(Number(localStorage.getItem("first")));
const openRssFeeds = ref(false);
const openUploadCSVModal = ref(false);
const fileUpload = ref();
const showMessage = ref({
  isShow: false,
  message: "",
});
const store = useStore();
const toast = useToast();
const confirm = useConfirm();
const router = useRouter();
const route = useRoute();
const modalPodcasterId = ref("");
const downloadingReports = ref({});
const token = localStorage.getItem("podcastAdminToken");
const podcastersTableFilter = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
});
const networkFilterId = computed(() => {
  const { networkId } = route.query;
  if (!networkId) {
    return null;
  }
  return Array.isArray(networkId) ? networkId[0] : networkId;
});
const networkFilterName = computed(() => {
  const { networkName } = route.query;
  if (!networkName) {
    return null;
  }
  return Array.isArray(networkName) ? networkName[0] : networkName;
});
const sampleCSVDownload = ref([
  {
    name: "John Doe",
    email: "emailaddress@example.com",
    avg_per_month: 10000,
    rss: "https://samplesite.com/rss-feed-url-1",
  },
  {
    name: "",
    email: "",
    avg_per_month: 11000,
    rss: "https://samplesite.com/rss-feed-url-2",
  },
  {
    name: "",
    email: "",
    avg_per_month: 12000,
    rss: "https://samplesite.com/rss-feed-url-3",
  },
]);

const switchTab = (tab) => {
  activeTab.value = tab;
  if (tab === "pending") {
    fetchPendingPodcasters();
  } else {
    fetchPodcasters();
  }
};

const activeTab = ref("all");
const pendingPodcasters = ref([]);
const approveModalVisible = ref(false);
const approveModalRss = ref("");
const approveModalType = ref("individual");
const approveModalPlan = ref("jamx network");
const approveModalRevShare = ref(0.25);
const approveModalHosted = ref("");
const approveModalAvgDownloads = ref(null);
const approveModalCampaigns = ref({
  promotion: {
    enabled: false,
    interval: null,
  },
  spotify: {
    enabled: false,
    interval: null,
  },
});
const approveModalError = ref("");
const approveModalPodcaster = ref(null);
const approveRejectLoading = ref(false);
const rejectConfirmVisible = ref(false);
const rejectModalPodcaster = ref(null);
const bankingDownloadAll = ref(false);
const bankingDownloadNumber = ref(10);
const openBankingDownloadModal = ref(false);
const bankingDownloadLoading = ref(false);
const sendEmailModalVisible = ref(false);
const selectedEmailTemplate = ref("");
const sendEmailPodcaster = ref(null);
const sendEmailLoading = ref(false);
const emailFormData = ref({
  url: "",
});

watch(approveModalAvgDownloads, (value) => {
  if (value > MAX_AVG_MONTHLY_DOWNLOADS) {
    approveModalAvgDownloads.value = MAX_AVG_MONTHLY_DOWNLOADS;
  }
});

const emailTemplateOptions = [
  {
    label: PODCASTER_JOIN_NETWORK_EMAIL_TEMPLATES.CALENDAR_LINK.label,
    value: PODCASTER_JOIN_NETWORK_EMAIL_TEMPLATES.CALENDAR_LINK.value,
  },
  {
    label: PODCASTER_JOIN_NETWORK_EMAIL_TEMPLATES.CONTRACT_LINK.label,
    value: PODCASTER_JOIN_NETWORK_EMAIL_TEMPLATES.CONTRACT_LINK.value,
  },
];

const templateRequirements = computed(() => {
  if (!selectedEmailTemplate.value) return [];

  const template = Object.values(PODCASTER_JOIN_NETWORK_EMAIL_TEMPLATES).find(
    (t) => t.value === selectedEmailTemplate.value
  );

  return template?.requires || [];
});

const isFormValid = computed(() => {
  if (!selectedEmailTemplate.value) return false;

  if (templateRequirements.value.includes("url")) {
    return emailFormData.value.url && emailFormData.value.url.trim() !== "";
  }

  return true;
});

const fetchPendingPodcasters = async () => {
  try {
    const response = await axios.get(
      API_BASE_URL + "/podcaster-join/pending-podcasters",
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    pendingPodcasters.value = Array.isArray(response.data.data)
      ? response.data.data.sort((a, b) => {
          return b.id - a.id;
        })
      : [];
  } catch (error) {
    console.error("Error fetching pending podcasters:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch pending podcasters",
      life: 4000,
    });
  }
};

const displayedPodcasters = computed(() => {
  return activeTab.value === "all"
    ? filteredPodcasters.value
    : pendingPodcasters.value;
});

const clearNetworkFilter = () => {
  const newQuery = { ...route.query };
  delete newQuery.networkId;
  delete newQuery.networkName;
  router.replace({
    name: "PodcastersList",
    query: newQuery,
  });
};

const goToCreateNewPodcaster = () => {
  router.push({ name: "AddPodcaster" });
};

const goToUpdatePodcaster = (podcasterId) => {
  router.push({
    name: "UpdatePodcaster",
    params: { podcasterId },
  });
};

const goToPodcasterDashboard = (podcasterData) => {
  router.push({
    name: "PodcastAdminPodcasterDashboard",
    query: {
      podcasterId: podcasterData.podcaster_id,
      connectedPublisherId: podcasterData.connected_publisher_id,
      plan: podcasterData.plan,
    },
  });
};

const goToPodcasterOverview = (podcasterId) => {
  router.push({ name: "PodcasterOverview", params: { podcasterId } });
};

const getAffiliateLink = (podcasterId) => {
  const link = "https://jamx.ai/podcasters-offer?ref_id=" + podcasterId;
  copyToClipboard(link, "Affiliate link copied to clipboard");
};

const getPendingPodcasterId = (podcaster) =>
  podcaster?.pending_podcaster_id ?? null;

const copyBankingLink = (pendingPodcasterId) => {
  if (!pendingPodcasterId) {
    toast.add({
      severity: "warn",
      summary: "Missing id",
      detail: "Pending podcaster id is not available",
      life: 3000,
    });
    return;
  }
  const link = `https://platform.jamx.ai/#/podcaster-join/banking?pending_podcaster_id=${pendingPodcasterId}`;
  copyToClipboard(link, "Banking link copied to clipboard");
};

const copyToClipboard = (text, successMessage = "Link copied to clipboard") => {
  navigator.clipboard.writeText(text).then(
    function () {
      toast.add({
        severity: "success",
        summary: "Success",
        detail: successMessage,
        life: 4000,
      });
    },
    function () {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to copy to clipboard",
        life: 4000,
      });
    }
  );
};

const goToShows = (podcasterId) => {
  router.push({
    name: "PodcastAdminShowsList",
    query: { podcasterId },
  });
};

const goToCampaigns = (podcasterId) => {
  localStorage.setItem("first", tempFirst);
  router.push({
    name: "PodcastAdminCampaignsList",
    query: { podcasterId },
  });
};

const onChangePage = (e) => {
  tempFirst = e.first;
  localStorage.removeItem("first");
};

const showUploadCSVModal = (flag) => {
  openUploadCSVModal.value = flag;
  showMessage.value.isShow = false;
};

const onSelect = () => {
  if (
    fileUpload.value.files.length > 0 ||
    fileUpload.value.files[0].type == "text/csv"
  )
    showMessage.value.isShow = false;
  else {
    showMessage.value.isShow = true;
    showMessage.value.message = "Please select CSV file";
  }
};

const submitCSV = async () => {
  if (
    fileUpload.value.files.length == 0 ||
    fileUpload.value.files[0].type != "text/csv"
  ) {
    showMessage.value.isShow = true;
    showMessage.value.message = "Please select CSV file";
    return;
  }
  try {
    let results = await new Promise((resolve, reject) => {
      Papa.parse(fileUpload.value.files[0], {
        complete: resolve,
        error: reject,
        skipEmptyLines: true,
      });
    });

    results = results.data.filter((result) => result.length == 4);
    if (results.length < 2 || results[0].length != 4) {
      showMessage.value.isShow = true;
      showMessage.value.message = "Wrong CSV format!";
      return;
    }
    let name = results[1][0];
    let email = results[1][1];
    let shows = [];
    for (let i = 1; i < results.length; i++) {
      shows.push({
        avg_per_month: results[i][2],
        rss: results[i][3],
      });
    }
    store.commit("setLoading", true);

    try {
      const response = await axios.post(
        API_BASE_URL + "/podcaster/csv-submit",
        {
          name,
          email,
          shows,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.success) {
        toast.add({
          severity: "success",
          summary: "Success",
          detail: `Shows created successfully`,
          life: 4000,
        });
        store.dispatch("loadPodcasters", { token });
        showUploadCSVModal();
      } else
        toast.add({
          severity: "error",
          summary: "Server error...",
          life: 4000,
        });
    } catch (error) {
      console.log(error);
      toast.add({
        severity: "error",
        summary: "Server error...",
        life: 4000,
      });
    }
  } catch (error) {
    console.error("Error parsing CSV:", error);
  }
  store.commit("setLoading", false);
};

const podcastersDownload = computed(() => {
  if (podcasters.value.length > 0) {
    return podcasters.value.map((item) => ({
      ...item,
      rss_urls: item.rss_urls
        ? item.rss_urls.map((r) => `${r.rss_url}(${r.author})`).join(",")
        : [],
    }));
  }
  return [];
});

const podcasters = computed(() => store.getters.podcasters);
const filteredPodcasters = computed(() => {
  const allPodcasters = podcasters.value || [];
  if (!networkFilterId.value) {
    return allPodcasters;
  }
  const targetId = networkFilterId.value.toString();
  return allPodcasters.filter((item) => {
    const networkId = item.network_id ? item.network_id.toString() : "";
    return networkId === targetId;
  });
});

const fetchPodcasters = async () => {
  if (!store.getters.podcasters || store.getters.podcasters.length == 0) {
    store.dispatch("loadPodcasters", { token });
  }

  console.log(
    store.getters.podcasters.filter((item) => item.email.includes("ilove"))
  );
};

const handleDeletePodcaster = async (podcaster_id) => {
  try {
    const response = await axios.delete(
      API_BASE_URL + "/podcaster/" + podcaster_id,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success) {
      toast.add({
        severity: "success",
        summary: "Success",
        detail: `Podcaster has been deleted`,
        life: 4000,
      });
      store.dispatch("loadPodcasters", { token });
    } else
      toast.add({
        severity: "error",
        summary: "Podcaster not found",
        life: 4000,
      });
  } catch (error) {
    console.log(error);
    toast.add({
      severity: "error",
      summary: "Server error...",
      life: 4000,
    });
  }
};

const downloadReport = async (podcaster_id, format = "csv") => {
  let response;
  downloadingReports.value[podcaster_id] = true;

  try {
    response = await axios.get(
      `${API_BASE_URL}/admin/reports/performance/${podcaster_id}?format=${format}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      }
    );

    const file = new Blob([response.data], {
      type: format === "pdf" ? "application/pdf" : "text/csv",
    });

    const fileURL = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = fileURL;
    link.download = `report_${podcaster_id}.${format}`;
    link.click();

    URL.revokeObjectURL(fileURL);
  } catch (error) {
    console.error("Error downloading report: ", error);
    toast.add({
      severity: "error",
      summary: "Error downloading report",
      life: 4000,
    });
  } finally {
    downloadingReports.value[podcaster_id] = false;
  }
};

const confirmDeletePodcaster = (podcaster_id) => {
  confirm.require({
    message: "Do you want to delete this podcaster?",
    header: "Delete Confirmation",
    icon: "pi pi-info-circle",
    acceptClass: "p-button-danger",
    accept: () => {
      handleDeletePodcaster(podcaster_id);
    },
  });
};

const showRSSFeeds = (podcasterId) => {
  modalPodcasterId.value = podcasterId;
  openRssFeeds.value = true;
};

const rssFeedsForModal = computed(() => {
  if (activeTab.value === "all") {
    const podcaster = podcasters.value.find(
      ({ podcaster_id }) => podcaster_id == modalPodcasterId.value
    );
    return podcaster?.rss_urls || [];
  } else {
    const podcaster = pendingPodcasters.value.find(
      ({ id }) => id == modalPodcasterId.value
    );

    if (!podcaster || !podcaster.rss_url) {
      return [];
    }

    return [
      {
        rss_url: podcaster.rss_url,
        author: podcaster.author || "",
      },
    ];
  }
});

const handleBankingDownload = async () => {
  bankingDownloadLoading.value = true;
  const limit = bankingDownloadAll.value ? null : bankingDownloadNumber.value;

  try {
    const response = await axios.get(
      `${API_BASE_URL}/podcasters/banking-details/${limit || "all"}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.success && response.data?.data.length > 0) {
      const csvData = response.data.data;

      const csvContent = [
        Object.keys(csvData[0]),
        ...csvData.map((row) => Object.values(row)),
      ]
        .map((row) => row.map((field) => `"${field || ""}"`).join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `banking-details-${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      openBankingDownloadModal.value = false;

      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Banking details downloaded successfully",
        life: 4000,
      });
    } else {
      toast.add({
        severity: "warn",
        summary: "No Data",
        detail: "No banking details found",
        life: 4000,
      });
    }
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to download banking details",
      life: 4000,
    });
  } finally {
    bankingDownloadLoading.value = false;
  }
};

const openApproveModal = (podcaster) => {
  approveModalPodcaster.value = podcaster;
  approveModalRss.value = "";
  approveModalType.value = "individual";
  approveModalPlan.value = "jamx network";
  approveModalRevShare.value = 0.25;
  approveModalHosted.value = "";
  approveModalAvgDownloads.value = podcaster.avg_monthly_downloads
    ? Number(podcaster.avg_monthly_downloads)
    : null;
  approveModalCampaigns.value = {
    promotion: {
      enabled: false,
      interval: null,
    },
    spotify: {
      enabled: false,
      interval: null,
    },
  };
  approveModalError.value = "";
  approveModalVisible.value = true;
};

const validateApprovalForm = () => {
  if (!approveModalRss.value.trim()) {
    return "RSS feed link is required";
  }

  try {
    const url = new URL(approveModalRss.value.toLowerCase().trim());
    if (!/^https?:/.test(url.protocol)) {
      throw new Error();
    }
  } catch {
    return "Please enter a valid RSS feed link (URL)";
  }

  if (!approveModalType.value) {
    return "Type is required";
  }

  if (!approveModalPlan.value) {
    return "Plan is required";
  }

  const normalizedAvgDownloads =
    approveModalAvgDownloads.value == null ||
    approveModalAvgDownloads.value === ""
      ? null
      : Number(approveModalAvgDownloads.value);

  if (!Number.isFinite(normalizedAvgDownloads) || normalizedAvgDownloads <= 0) {
    return "Avg monthly downloads is required and must be greater than 0";
  }

  if (!approveModalHosted.value) {
    return "Hosted type is required";
  }

  if (approveModalRevShare.value < 0) {
    return "Revenue share must be 0 or greater";
  }

  const hasSelectedCampaign =
    approveModalCampaigns.value.promotion.enabled ||
    approveModalCampaigns.value.spotify.enabled;
  if (!hasSelectedCampaign) {
    return "At least one campaign type must be selected";
  }

  if (approveModalCampaigns.value.promotion.enabled) {
    if (!approveModalCampaigns.value.promotion.interval) {
      return "Promotion interval must be greater than 0";
    }
  }

  if (approveModalCampaigns.value.spotify.enabled) {
    if (!approveModalCampaigns.value.spotify.interval) {
      return "Spotify interval must be greater than 0";
    }
  }

  return null;
};

const handleApprove = async () => {
  const validationError = validateApprovalForm();
  if (validationError) {
    approveModalError.value = validationError;
    return;
  }

  approveModalVisible.value = false;
  approveRejectLoading.value = true;

  const normalizedAvgDownloads =
    approveModalAvgDownloads.value == null ||
    approveModalAvgDownloads.value === ""
      ? null
      : Number(approveModalAvgDownloads.value);

  try {
    const approvedPendingPodcasterId =
      approveModalPodcaster.value.pending_podcaster_id;
    const payload = {
      newRssUrl: approveModalRss.value.trim(),
      type: approveModalType.value,
      plan: approveModalPlan.value,
      revshare: approveModalRevShare.value,
      hosted: approveModalHosted.value,
      avgMonthlyDownloads: normalizedAvgDownloads,
      campaigns: [
        ...(approveModalCampaigns.value.promotion.enabled
          ? [
              {
                type: "promotion",
                interval: approveModalCampaigns.value.promotion.interval,
              },
            ]
          : []),
        ...(approveModalCampaigns.value.spotify.enabled
          ? [
              {
                type: "spotify",
                interval: approveModalCampaigns.value.spotify.interval,
              },
            ]
          : []),
      ],
    };

    await axios.post(
      `${API_BASE_URL}/podcaster-join/pending-podcaster/${approvedPendingPodcasterId}/approve`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.add({
      severity: "success",
      summary: "Podcaster approved",
      life: 3000,
    });
    await fetchPendingPodcasters();
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error approving podcaster",
      detail:
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Server error",
      life: 4000,
    });
  } finally {
    approveRejectLoading.value = false;
  }
};

const handleApproveCancel = () => {
  approveModalVisible.value = false;
  approveModalError.value = "";
};

const openRejectConfirm = (podcaster) => {
  rejectModalPodcaster.value = podcaster;
  rejectConfirmVisible.value = true;
};

const handleReject = async () => {
  rejectConfirmVisible.value = false;
  approveRejectLoading.value = true;
  try {
    await axios.post(
      `${API_BASE_URL}/podcaster-join/pending-podcaster/${rejectModalPodcaster.value.pending_podcaster_id}/reject`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    toast.add({
      severity: "success",
      summary: "Podcaster rejected",
      life: 3000,
    });
    await fetchPendingPodcasters();
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error rejecting podcaster",
      detail: error.response?.data?.error || "Server error",
      life: 4000,
    });
  } finally {
    approveRejectLoading.value = false;
  }
};

const handleRejectCancel = () => {
  rejectConfirmVisible.value = false;
};

// Send Email Modal Functions
const openSendEmailModal = (podcaster) => {
  sendEmailPodcaster.value = podcaster;
  selectedEmailTemplate.value = "";
  emailFormData.value = { url: "" };
  sendEmailModalVisible.value = true;
};

const onTemplateChange = () => {
  emailFormData.value = { url: "" };
};

const handleSendEmailSubmit = async () => {
  sendEmailLoading.value = true;

  try {
    const payload = {
      template: selectedEmailTemplate.value,
      pendingPodcasterId: sendEmailPodcaster.value.pending_podcaster_id,
      ...emailFormData.value,
    };

    await axios.post(
      `${API_BASE_URL}/podcaster-join/pending-podcaster/send-email`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.add({
      severity: "success",
      summary: "Email sent",
      detail: `Email has been sent to ${sendEmailPodcaster.value.name}`,
      life: 4000,
    });

    sendEmailModalVisible.value = false;
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error sending email",
      detail: "Failed to send email",
      life: 4000,
    });
  } finally {
    sendEmailLoading.value = false;
    fetchPendingPodcasters();
  }
};

const handleSendEmailCancel = () => {
  sendEmailModalVisible.value = false;
};

const fetchShows = () => {
  if (!store.getters.shows || store.getters.shows.length == 0) {
    store.dispatch("loadShows", { token });
  }
};

const viewPodcaster = (email) => {
  activeTab.value = "all";
  podcastersTableFilter.value.global.value = email;
};

onBeforeMount(() => {
  if (route.query.search) {
    podcastersTableFilter.value.global.value = route.query.search;
    activeTab.value = "all";
  }
  if (networkFilterId.value) {
    activeTab.value = "all";
  }
  fetchPodcasters();
  fetchShows();
});
</script>

<style scoped>
.p-datatable .p-datatable-thead > tr > th,
.p-datatable .p-datatable-tbody > tr > td {
  padding: 0.5rem;
  font-size: 0.9rem;
}

.p-card .p-card-body {
  padding: 0.5rem;
}

.p-dropdown,
.p-inputtext,
.p-button.p-button-sm {
  font-size: 0.9rem;
}

.p-button.p-button-sm {
  padding: 0.3rem 0.5rem;
}

.truncate-text {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.date-cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.p-datatable .p-datatable-tbody > tr > td {
  max-height: 3.5em;
  overflow: hidden;
}
</style>
